/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { AccordionCard } from '@modernloop/shared/components';
import { useFeatureFlagNumber, useFlag } from '@modernloop/shared/feature-flag';
import { CircleWithCheckIcon, RadioIcon, SendIcon, WarningIcon } from '@modernloop/shared/icons';
import { reportError } from '@modernloop/shared/observability';
import { Alert, Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  AtsNotSchedulableReason,
  InterviewPlan_JobStageFragment,
  Schedule_ApplicationFragment,
  Schedule_CandidateFragment,
  Schedule_InterviewPlanFragment,
  Schedule_JobStageFragment,
  Schedule_ScheduleFragment,
  Schedule_SelfScheduleRequestFragment,
  UseGetInterviewNames_InterviewsFragment,
  useScheduleTaskInterviewPlanUpsertMutation,
} from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import AtsName from 'src/entities/Ats/AtsName';
import InterviewPlan from 'src/entities/InterviewPlan';
import getSaveInterviewPlanInput from 'src/entities/InterviewPlan/getSaveInterviewPlanInput';
import useIsInterviewPlanValid, {
  INCOMPLETE_INTERVIEW_PLAN,
  INTERVIEW_PLAN_WITH_DYNAMIC_BREAKS,
  INTERVIEW_PLAN_WITH_MULTI_DAY_SCHEDULE,
} from 'src/entities/InterviewPlan/useIsInterviewPlanValid';
import { useResetInterviewPlanToBaseJobStage } from 'src/entities/InterviewPlan/useResetInterviewPlanToBaseJobStage';
import ScheduleContentEntity from 'src/entities/ScheduleContent';

import { useIsMLoopInternalOnly } from 'src/hooks/feature-flag/useIsMLoopInternalOnly';
import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useApplicationJobStageSchedulable from 'src/hooks/useApplicationJobStageSchedulable';
import useHistory from 'src/hooks/useHistory';
import useIsAiTaskEnabled from 'src/hooks/useIsAiTaskEnabled';
import useIsApplicationRejected from 'src/hooks/useIsApplicationRejected';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';
import { useSerializedMutation } from 'src/hooks/useSerializedMutation';
import { SearchParamKnownKeys } from 'src/hooks/useUrlSearchParams';

import getTaskSuggestedSchedulesUrl from 'src/urls/getTaskSuggestedScheduleUrl';

import IncompleteInterviewPlanAlert from 'src/views-new/SelfSchedule/IncompleteInterviewPlanAlert';
import SelfSchedule from 'src/views-new/SelfSchedule/SelfSchedule';
import SelfScheduleOpenAlert from 'src/views-new/SelfSchedule/SelfScheduleOpenAlert';
import { SelfScheduleOptionsModalPage } from 'src/views-new/SelfSchedule/SelfScheduleOptionsModal';
import SelfScheduleOptionsModalWrapper from 'src/views-new/SelfSchedule/SelfScheduleOptionsModalWrapper';
import { SidePanelManager } from 'src/views-new/sidepanel/common/SidePanelManager';

import RequirementContainer from '../Common/RequirementContainer';

import TaskMeetingLocation from './TaskMeetingLocation';
import useGetInterviewNames from './utils/useGetInterviewNames';

const useGetScheduleInterviewNames = (schedule?: Schedule_ScheduleFragment) => {
  const result = useMemo(() => {
    return schedule?.applicationStageInterviews?.map((interview) => interview.name).join(', ');
  }, [schedule]);

  return result || undefined;
};

type Fragments = {
  application: Schedule_ApplicationFragment;
  candidate: Schedule_CandidateFragment;
  interviewPlan: Schedule_InterviewPlanFragment;
  jobStage: Schedule_JobStageFragment;
  selfScheduleRequest?: Schedule_SelfScheduleRequestFragment;
  schedule?: Schedule_ScheduleFragment;
};

type Props = {
  taskId: string;
  jobStageId: string;
  customJobStageId: string;
  availabilityRequestId?: string;
  availabilityRequestedAfterSchedule: boolean;
  availabilityReceivedAfterSchedule: boolean;
};

const Schedule: FCWithFragments<Fragments, Props> = ({
  availabilityRequestId,
  application,
  jobStage,
  candidate,
  taskId,
  jobStageId,
  customJobStageId,
  interviewPlan: interviewPlanProps,
  selfScheduleRequest,
  schedule,
  availabilityRequestedAfterSchedule,
  availabilityReceivedAfterSchedule,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { isSchedulable, reason } = useApplicationJobStageSchedulable(
    {
      application,
    },
    { jobStageId }
  );

  const [savingInterviewPlan, setSavingInterviewPlan] = useState(false);
  const [interviewPlan, setInterviewPlan] = useState<InterviewPlan_JobStageFragment>(interviewPlanProps);

  const [showSelfScheduleOptionsModal, setShowSelfScheduleOptionsModal] = useState(false);
  const [showIncompleteInterviewPlanAlert, setShowIncompleteInterviewPlanAlert] = useState(false);
  const [showSelfScheduleDialogs, setShowSelfScheduleDialogs] = useState(false);
  const [showSelfScheduleOpenAlert, setShowSelfScheduleOpenAlert] = useState(false);
  const isApplicationRejected = useIsApplicationRejected(application.status);
  const isReadonlyInterviewPlan = Boolean(selfScheduleRequest?.id) || isApplicationRejected;
  const isReadonlyMeetingLocation = Boolean(selfScheduleRequest?.id || schedule?.id);
  const aiTaskAgentEnabled = useIsAiTaskEnabled();

  const refetchactivityFeed = useActivityFeedRefetch();
  const refetchScheduleTaskQuery = useRefetchScheduleTaskQuery();

  const { resetPlan } = useResetInterviewPlanToBaseJobStage({
    jobStageId: jobStage?.id,
    customJobStageId,
  });
  const collaborationLinkEnabled = useFlag('prd-155-codesignal-template-select');

  const interviewPlanAutoSaveDebounceTimeout = useFeatureFlagNumber('user_interview_plan_auto_save_debounce_timeout');
  const orgBreakImprovementsEnabled = useFlag('org_break_improvements');
  const isMLoopInternalOnly = useIsMLoopInternalOnly();

  const [scheduleTaskInterviewPlanUpsertMutation] = useScheduleTaskInterviewPlanUpsertMutation();

  const saveInterviewPlanMutation = useCallback(
    async (planToSave: InterviewPlan_JobStageFragment) => {
      const interviewPlanInput = getSaveInterviewPlanInput(
        { interviewPlan: planToSave },
        { finalJobStageId: interviewPlan.id, collaborationLinkEnabled }
      );

      await scheduleTaskInterviewPlanUpsertMutation({
        variables: {
          input: orgBreakImprovementsEnabled
            ? interviewPlanInput
            : { jobStageId: interviewPlanInput.jobStageId, groups: interviewPlanInput.groups },
        },
      });
    },
    [collaborationLinkEnabled, interviewPlan.id, orgBreakImprovementsEnabled, scheduleTaskInterviewPlanUpsertMutation]
  );

  const { trigger: saveInterviewPlan } = useSerializedMutation(saveInterviewPlanMutation, {
    debounceMs: interviewPlanAutoSaveDebounceTimeout,
    onStart: () => setSavingInterviewPlan(true),
    onSuccess: () =>
      enqueueSnackbar(t('hiring-dashboard-tasks:tasks.schedule.interviewPlanSaved'), {
        variant: 'success',
        preventDuplicate: true,
      }),
    onError: () =>
      enqueueSnackbar(t('hiring-dashboard-tasks:tasks.schedule.failedToSaveInterviewPlan'), {
        variant: 'error',
        preventDuplicate: true,
      }),
    onComplete: () => setSavingInterviewPlan(false),
  });

  const interviewNames = useGetInterviewNames({
    interviews:
      interviewPlan.jobStageInterviewGroups
        ?.map((group) => (group.jobStageInterviews as UseGetInterviewNames_InterviewsFragment[]) || [])
        .flat() || [],
  });

  const scheduledInterviewNames = useGetScheduleInterviewNames(schedule);
  let alertText = availabilityRequestedAfterSchedule
    ? t('hiring-dashboard-tasks:tasks.schedule.newAvailabilityRequestedAfterSchedule')
    : undefined;

  const history = useHistory();

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    if (!isReadonlyInterviewPlan) {
      return;
    }
    setInterviewPlan(interviewPlanProps);
  }, [interviewPlanProps, isReadonlyInterviewPlan]);

  const icon = useMemo((): React.ReactNode => {
    if (availabilityRequestedAfterSchedule || availabilityReceivedAfterSchedule) {
      return <WarningIcon color="warning" />;
    }
    if (selfScheduleRequest?.id && !schedule?.id) {
      return <SendIcon color="warning" />;
    }
    if (schedule?.id) {
      return <CircleWithCheckIcon color="success" />;
    }
    return <RadioIcon />;
  }, [schedule?.id, selfScheduleRequest?.id, availabilityRequestedAfterSchedule, availabilityReceivedAfterSchedule]);

  const handleScheduleNow = useCallback(() => {
    refetchScheduleTaskQuery();
    refetchactivityFeed();
    SidePanelManager.closeSidePanel();
    history.push(`/schedule/${application.id}?${SearchParamKnownKeys.taskId}=${taskId}`);
  }, [refetchactivityFeed, application.id, history, refetchScheduleTaskQuery, taskId]);

  const handleGotoTaskSuggestedSchedulesPage = useCallback(() => {
    const suggestedSchedulesUrl = getTaskSuggestedSchedulesUrl(taskId);
    history.push(suggestedSchedulesUrl);
    SidePanelManager.closeSidePanel();
  }, [history, taskId]);

  const isInterviewPlanValidResult = useIsInterviewPlanValid(
    { jobStage: interviewPlan },
    {
      isDayBreakAllowed: false,
      allowSomeInterviewsHiddenFromCandiate: false,
      allowDynamicBreaks: false,
    }
  );

  const handleShowSelfScheduleOptionsModal = useCallback(async () => {
    if (!availabilityRequestId && !isInterviewPlanValidResult.isValid) {
      setShowIncompleteInterviewPlanAlert(true);
      return;
    }
    await refetchScheduleTaskQuery();
    setShowSelfScheduleOptionsModal(true);
  }, [availabilityRequestId, isInterviewPlanValidResult.isValid, refetchScheduleTaskQuery]);

  if (availabilityReceivedAfterSchedule) {
    alertText = t('hiring-dashboard-tasks:tasks.schedule.newAvailabilityReceivedAfterSchedule');
  }

  const resetInterviewPlan = async () => {
    try {
      const newInterviewPlan = await resetPlan();
      if (newInterviewPlan) {
        setInterviewPlan(newInterviewPlan);
        enqueueSnackbar(t('hiring-dashboard-tasks:tasks.schedule.interviewPlanSaved'), {
          variant: 'success',
          preventDuplicate: true,
        });
      }
    } catch (e) {
      reportError(e);
      enqueueSnackbar(t('hiring-dashboard-tasks:tasks.schedule.failedToResetInterviewPlan'), {
        variant: 'error',
        preventDuplicate: true,
      });
    }
  };
  const getScheduleDiabledButtonTooltip = (): string | JSX.Element => {
    let toolTip: string | JSX.Element = '';
    if (isApplicationRejected) {
      toolTip = t('hiring-dashboard-tasks:tasks.requirements.applicationRejected');
    } else if (!isSchedulable) {
      if (reason === AtsNotSchedulableReason.NotSchedulable) {
        if (application.jobStageId !== jobStageId) {
          toolTip = t('hiring-dashboard-tasks:tasks.schedule.tasksMustBeOnCurrentStage');
        } else {
          toolTip = (
            <>
              {t('hiring-dashboard-tasks:tasks.schedule.unschedulableStageInAts')} <AtsName />
            </>
          );
        }
      } else if (reason === AtsNotSchedulableReason.NotSchedulableOnCurrentStep) {
        const step =
          // eslint-disable-next-line no-underscore-dangle
          application.atsApplication?.atsFields?.__typename === 'WorkdayApplication'
            ? application.atsApplication?.atsFields?.currentStepName
            : '';
        if (step) {
          toolTip = t('hiring-dashboard-tasks:tasks.schedule.candidateOnStep', { step });
        }
      }
    }
    return toolTip;
  };

  return (
    <>
      <RequirementContainer title={t('hiring-dashboard-tasks:tasks.schedule.scheduleInterviews')} icon={icon}>
        <Stack spacing={1}>
          {alertText && (
            <Alert severity="warning">
              <Typography variant="body1">{alertText}</Typography>
            </Alert>
          )}
          <AccordionCard
            title={
              <Typography>
                {schedule
                  ? scheduledInterviewNames || t('hiring-dashboard-tasks:tasks.schedule.interviews')
                  : interviewNames}
              </Typography>
            }
            accordionDetailsPadding={schedule ? 0 : undefined}
          >
            <Stack spacing={1}>
              {selfScheduleRequest?.id && !schedule?.id && (
                <SelfSchedule
                  application={application}
                  applicationId={application.id}
                  candidateId={candidate.id}
                  jobStageId={jobStageId}
                  customJobStageId={customJobStageId}
                  taskSelfSchedule={selfScheduleRequest}
                  showSelfScheduleDialogs={showSelfScheduleDialogs}
                  onClose={(emailSent) => {
                    if (emailSent === undefined) {
                      setShowSelfScheduleDialogs(false);
                    } else if (typeof emailSent === 'boolean') {
                      setShowSelfScheduleDialogs(!emailSent);
                    }
                    setShowSelfScheduleDialogs(false);
                  }}
                  jobStageSettings={jobStage.jobStageSettingsV2 || undefined}
                />
              )}

              {!schedule?.id && (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Tooltip title={getScheduleDiabledButtonTooltip()}>
                    <span>
                      <Button
                        size="small"
                        color="primary"
                        disabled={isApplicationRejected || !isSchedulable}
                        data-testid="schedule-task-requirements-schedule-now-button"
                        onClick={() => {
                          if (selfScheduleRequest?.id) {
                            setShowSelfScheduleOpenAlert(true);
                            return;
                          }
                          handleScheduleNow();
                        }}
                      >
                        {t('hiring-dashboard-tasks:tasks.schedule.scheduleNow')}
                      </Button>
                    </span>
                  </Tooltip>

                  {!selfScheduleRequest?.id && (
                    <Tooltip title={getScheduleDiabledButtonTooltip()}>
                      <span>
                        <Button
                          data-testid="schedule-task-requirements-send-self-schedule-link-button"
                          size="small"
                          disabled={savingInterviewPlan || isApplicationRejected || !isSchedulable}
                          onClick={handleShowSelfScheduleOptionsModal}
                        >
                          {t('hiring-dashboard-tasks:tasks.schedule.sendSelfScheduleLink')}
                        </Button>
                      </span>
                    </Tooltip>
                  )}

                  {isMLoopInternalOnly ? (
                    <Tooltip title={t('hiring-dashboard-tasks:tasks.schedule.mlOnly')}>
                      <Button
                        key="schedule-content-actions-goto-suggested-schedules-button"
                        size="small"
                        onClick={handleGotoTaskSuggestedSchedulesPage}
                      >
                        {t('hiring-dashboard-tasks:tasks.schedule.gotoSuggestedSchedules')}
                      </Button>
                    </Tooltip>
                  ) : null}
                </Stack>
              )}

              {!schedule?.id && (
                <Box ml="-8px">
                  <InterviewPlan
                    onResetToJobStageDefault={resetInterviewPlan}
                    schedulable={false}
                    job={interviewPlanProps.job || undefined}
                    jobStage={interviewPlan}
                    readonly={isReadonlyInterviewPlan}
                    onUpdated={(newInterviewPlan: InterviewPlan_JobStageFragment) => {
                      setInterviewPlan(newInterviewPlan);
                      saveInterviewPlan(newInterviewPlan);
                    }}
                  />
                </Box>
              )}

              {!schedule?.id && aiTaskAgentEnabled && (
                <TaskMeetingLocation taskId={taskId} disabled={isReadonlyMeetingLocation} />
              )}

              {schedule?.id && (
                <Box overflow="hidden">
                  <ScheduleContentEntity application={application} schedule={schedule} />
                </Box>
              )}
            </Stack>
          </AccordionCard>
        </Stack>
      </RequirementContainer>

      {showSelfScheduleOptionsModal && (
        <SelfScheduleOptionsModalWrapper
          isAvailabilityRequested={Boolean(availabilityRequestId)}
          applicationId={application.id}
          jobId={application.job?.id}
          candidateId={candidate.id}
          jobStageId={jobStageId}
          customJobStageId={customJobStageId}
          taskId={taskId}
          selfSchedule={selfScheduleRequest}
          jobStageSettingsV2={jobStage.jobStageSettingsV2 || undefined}
          selfScheduleOptionsModalPage={SelfScheduleOptionsModalPage.OPTIONS}
          onClose={(emailSent) => {
            setShowSelfScheduleOptionsModal(false);
            if (emailSent === undefined) {
              setShowSelfScheduleDialogs(false);
            } else {
              setShowSelfScheduleDialogs(!emailSent);
            }
          }}
          interviewMeetingLocation={
            selfScheduleRequest?.interviewMeetingLocationSetting ||
            jobStage.jobStageSettingsV2?.interviewMeetingLocationSettingWithLevel?.interviewMeetingLocationSetting ||
            undefined
          }
        />
      )}

      {!availabilityRequestId && !isInterviewPlanValidResult.isValid && showIncompleteInterviewPlanAlert && (
        <IncompleteInterviewPlanAlert
          title={isInterviewPlanValidResult.title}
          message={
            isInterviewPlanValidResult.title === INCOMPLETE_INTERVIEW_PLAN ||
            isInterviewPlanValidResult.title === INTERVIEW_PLAN_WITH_MULTI_DAY_SCHEDULE ||
            isInterviewPlanValidResult.title === INTERVIEW_PLAN_WITH_DYNAMIC_BREAKS
              ? isInterviewPlanValidResult.error
              : t('hiring-dashboard-tasks:tasks.schedule.selfScheduleUnavailableHiddenEvents')
          }
          onClose={() => setShowIncompleteInterviewPlanAlert(false)}
        />
      )}

      {showSelfScheduleOpenAlert && selfScheduleRequest?.id && (
        <SelfScheduleOpenAlert
          selfScheduleOptionId={selfScheduleRequest.id}
          onClose={() => setShowSelfScheduleOpenAlert(false)}
          onContinue={handleScheduleNow}
        />
      )}
    </>
  );
};

Schedule.fragments = {
  application: gql`
    ${ScheduleContentEntity.fragments.application}
    ${useApplicationJobStageSchedulable.fragments.application}
    fragment Schedule_application on Application {
      id
      status
      atsType
      atsApplication {
        atsId
        atsFields {
          ... on WorkdayApplication {
            currentStepName
            __typename
          }
          __typename
        }
      }
      job {
        id
      }
      ...ScheduleContent_application
      ...useApplicationJobStageSchedulable_application
    }
  `,
  candidate: gql`
    fragment Schedule_candidate on Candidate {
      id
    }
  `,
  interviewPlan: gql`
    ${InterviewPlan.fragments.job}
    ${InterviewPlan.fragments.jobStage}
    ${useGetInterviewNames.fragments.interviews}
    ${getSaveInterviewPlanInput.fragments.interviewPlan}
    ${useIsInterviewPlanValid.fragments.jobStage}
    fragment Schedule_interviewPlan on JobStage {
      id
      ...InterviewPlan_jobStage
      ...getSaveInterviewPlanInput_interviewPlan
      ...useIsInterviewPlanValid_jobStage
      job {
        id
        ...InterviewPlan_job
      }
      jobStageInterviewGroups {
        id
        jobStageInterviews {
          id
          ...useGetInterviewNames_interviews
        }
      }
    }
  `,
  jobStage: gql`
    ${SelfScheduleOptionsModalWrapper.fragments.interviewMeetingLocation}
    ${SelfScheduleOptionsModalWrapper.fragments.jobStageSettingsV2}
    ${SelfSchedule.fragments.jobStageSettings}
    fragment Schedule_jobStage on JobStage {
      id
      jobStageSettingsV2 {
        ...SelfScheduleOptionsModalWrapper_jobStageSettingsV2
        ...SelfSchedule_jobStageSettings
        interviewMeetingLocationSettingWithLevel {
          interviewMeetingLocationSetting {
            ...SelfScheduleOptionsModalWrapper_interviewMeetingLocation
          }
        }
      }
    }
  `,
  selfScheduleRequest: gql`
    ${SelfScheduleOptionsModalWrapper.fragments.selfSchedule}
    ${SelfScheduleOptionsModalWrapper.fragments.interviewMeetingLocation}
    fragment Schedule_selfScheduleRequest on SelfScheduleOptions {
      ...SelfScheduleOptionsModalWrapper_selfSchedule
      interviewMeetingLocationSetting {
        ...SelfScheduleOptionsModalWrapper_interviewMeetingLocation
      }
    }
  `,
  schedule: gql`
    ${ScheduleContentEntity.fragments.schedule}
    fragment Schedule_schedule on ApplicationStage {
      ...ScheduleContent_schedule
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ScheduleTaskInterviewPlanUpsertMutation = gql`
  ${InterviewPlan.fragments.jobStage}
  ${getSaveInterviewPlanInput.fragments.interviewPlan}
  mutation ScheduleTaskInterviewPlanUpsert($input: JobStageInterviewPlanUpsertInput!) {
    jobStageInterviewPlanUpsert(input: $input) {
      jobStage {
        id
        ...InterviewPlan_jobStage
        ...getSaveInterviewPlanInput_interviewPlan
      }
    }
  }
`;

export default Schedule;
